import React, { useRef } from "react";
import { formatMoney, formatDate } from "../Helpers";
import { FaWhatsapp, FaDownload } from "react-icons/fa";

export default function Receipt({ saleData }) {
  const receiptRef = useRef();
  const receiptId = saleData?.uuid?.split("-")?.[0]?.toUpperCase() || "N/A";
  const saleTypeLabel = saleData?.saleType ? saleData.saleType.toUpperCase() : "SALE";
  const contractLabel =
    saleData?.saleType === "loan"
      ? `${saleData?.durationMonths || 0} months`
      : "Cash sale";
  const infoRows = [
    { label: "Customer", value: saleData?.fullname || "Not provided" },
    { label: "Phone", value: saleData?.phone || "Not provided" },
    { label: "Motorcycle", value: saleData?.model || "Not provided" },
    { label: "Registration", value: saleData?.regNumber || "Not set" },
    { label: "Sale Type", value: saleTypeLabel },
    { label: "Contract", value: contractLabel },
    { label: "Issued Date", value: saleData?.dateCreated ? formatDate(saleData.dateCreated) : "Not set" },
  ];

  // Handle WhatsApp Sharing
  const handleWhatsAppShare = () => {
    // Format phone number (remove spaces/dashes, ensure country code if needed)
    const cleanPhone = saleData?.phone?.replace(/\D/g, "") || "";
    
    const message = `Hi ${saleData.fullname}, here is your receipt from *BOB MAPIKIPIKI*.\n\n` +
                    `*Receipt ID:* ${receiptId}\n` +
                    `*Model:* ${saleData.model}\n` +
                    `*Amount paid:* ${formatMoney(saleData.price)}\n` +
                    `*Sale type:* ${saleTypeLabel}\n` +
                    `*Date:* ${formatDate(saleData.dateCreated)}\n\n` +
                    `Thank you for your business!`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Handle PDF Download using html2pdf.js
  const handleDownloadPDF = async () => {
    // Dynamically import html2pdf.js on the client side
    const html2pdf = (await import("html2pdf.js")).default;
    const element = receiptRef.current;

    const opt = {
      margin: 0.5,
      filename: `Receipt_${receiptId}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="mx-auto" style={{ width: "600px", maxWidth: "100%" }}>
      {/* Modern Quick Actions Bar - Hidden during PDF generation */}
      <div className="d-flex justify-content-end gap-2 mb-3 data-html2pdf-ignore no-print">
        <button 
          onClick={handleWhatsAppShare}
          className="btn btn-sm btn-outline-success d-flex align-items-center gap-2 rounded-pill px-3 py-2 fw-semibold"
        >
          <FaWhatsapp size={16} />
          Share to WhatsApp
        </button>
        
        <button 
          onClick={handleDownloadPDF}
          className="btn btn-sm btn-dark d-flex align-items-center gap-2 rounded-pill px-3 py-2 fw-semibold"
        >
          <FaDownload size={16} />
          Download PDF
        </button>
      </div>

      {/* Main Printable Receipt Element */}
      <div 
        ref={receiptRef}
        className="bg-white p-5 border-0 shadow-lg position-relative" 
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <div
          className="position-absolute top-0 start-0 w-100"
          style={{ height: "6px", background: "linear-gradient(90deg, #0f172a, #334155, #0f172a)" }}
        />

        {/* Brand Header */}
        <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap mb-5">
          <div className="text-center">
            <h2 className="fw-bolder m-0" style={{ letterSpacing: "-1px", color: "#1e293b" }}>
              BOB MAPIKIPIKI
            </h2>
            <p className="text-uppercase small fw-bold mb-2" style={{ letterSpacing: "2px", color: "#64748b" }}>
              Official Transaction Receipt
            </p>
            
          </div>
          <div className="text-md-end d-none">
            <div
              className="d-inline-block px-3 py-2 rounded-3"
              style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}
            >
              <small className="text-uppercase d-block" style={{ letterSpacing: "1.5px", color: "#64748b" }}>
                Receipt ID
              </small>
              <strong className="font-monospace" style={{ color: "#0f172a" }}>
                {receiptId}
              </strong>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-6">
            <div className="h-100 p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <small className="text-uppercase d-block mb-1" style={{ letterSpacing: "1.5px", color: "#64748b" }}>
                Customer
              </small>
              <strong style={{ color: "#0f172a" }}>{saleData?.fullname || "Not provided"}</strong>
            </div>
          </div>
          <div className="col-6">
            <div className="h-100 p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <small className="text-uppercase d-block mb-1" style={{ letterSpacing: "1.5px", color: "#64748b" }}>
                Sale Type
              </small>
              <strong style={{ color: "#0f172a" }}>{saleTypeLabel}</strong>
            </div>
          </div>
        </div>

        {/* Data Grid */}
        <div className="rounded-3 mb-5" style={{ border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div className="px-4 py-3 border-bottom" style={{ backgroundColor: "#f8fafc" }}>
            <h6 className="mb-0 fw-bold" style={{ color: "#0f172a" }}>
              Transaction Details
            </h6>
          </div>
          <div className="px-4 py-2">
            {infoRows.map((item) => (
              <div
                key={item.label}
                className="d-flex justify-content-between align-items-start gap-3 py-3 border-bottom"
                style={{ borderColor: "#e2e8f0" }}
              >
                <span className="small text-uppercase fw-semibold" style={{ letterSpacing: "1px", color: "#64748b" }}>
                  {item.label}
                </span>
                <strong className="text-end" style={{ color: "#0f172a" }}>
                  {item.value}
                </strong>
              </div>
            ))}
          </div>
        </div>

        {/* Modern Total Summary */}
        <div className="d-none bg-dark text-white p-4 rounded-3 d-flex justify-content-between align-items-center">
          <span className="text-uppercase small" style={{ letterSpacing: "1px" }}>Total Payable</span>
          <span className="fw-bolder fs-3">{formatMoney(saleData.price)}</span>
        </div>

        {/* Footer */}
        <div className="mt-5 text-center">
          <p className="small text-muted mb-1">
            Authorized By: <strong>{saleData?.sellerName || "System"}</strong>
          </p>
          <p className="small text-muted mb-0">&copy; {new Date().getFullYear()} BOB MAPIKIPIKI.</p>
        </div>
      </div>
    </div>
  );
}
